import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { notificationAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../components/ConfirmModal';
import {
  FiBell, FiCheck, FiTrash2, FiCheckSquare,
  FiFolder, FiAlertCircle, FiClock, FiMessageCircle,
  FiUser, FiRefreshCw, FiSend
} from 'react-icons/fi';
import './HR.css';

const TYPE_META = {
  task_assigned:   { icon: <FiCheckSquare />, color: '#667eea', label: 'Task Assigned' },
  task_completed:  { icon: <FiCheck />,       color: '#48bb78', label: 'Task Completed' },
  task_comment:    { icon: <FiMessageCircle />,color: '#ed8936', label: 'New Comment' },
  project_update:  { icon: <FiFolder />,      color: '#4299e1', label: 'Project Update' },
  project_assigned:{ icon: <FiFolder />,      color: '#667eea', label: 'Project Assigned' },
  deadline_reminder:{ icon: <FiAlertCircle />, color: '#f56565', label: 'Deadline Reminder' },
  deadline:        { icon: <FiAlertCircle />, color: '#f56565', label: 'Deadline Alert' },
  mention:         { icon: <FiUser />,         color: '#9f7aea', label: 'Mention' },
  status_change:   { icon: <FiClock />,        color: '#38b2ac', label: 'Status Change' },
  leave_request:   { icon: <FiClock />,        color: '#f59e0b', label: 'Leave Request' },
  leave_processed: { icon: <FiCheck />,        color: '#10b981', label: 'Leave Processed' },
};

const Notifications = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const confirm = useConfirm();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [loading, setLoading]             = useState(true);
  const [filter, setFilter]               = useState('all');
  const [announceModal, setAnnounceModal] = useState(false);
  const [announceForm, setAnnounceForm]   = useState({ title:'', message:'', targetRole:'all' });
  const [sending, setSending]             = useState(false);

  useEffect(() => { load(); }, [filter]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await notificationAPI.getAll({ unreadOnly: filter === 'unread', limit: 50 });
      setNotifications(r.data.data || []);
      setUnreadCount(r.data.unreadCount || 0);
    } catch { toast.error('Failed to load notifications'); }
    setLoading(false);
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationAPI.markRead(id);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
      // Tell sidebar to refresh badge immediately
      window.dispatchEvent(new CustomEvent('pm-notif-read'));
    } catch { toast.error('Failed to mark as read'); }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
      toast.success('All marked as read');
      // Tell sidebar to refresh badge immediately
      window.dispatchEvent(new CustomEvent('pm-notif-read'));
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try {
      await notificationAPI.deleteOne(id);
      const removed = notifications.find(n => n._id === id);
      setNotifications(prev => prev.filter(n => n._id !== id));
      if (removed && !removed.isRead) setUnreadCount(c => Math.max(0, c - 1));
    } catch { toast.error('Failed to delete'); }
  };

  const handleDeleteAll = async () => {
    const ok = await confirm('Clear all notifications?', { title: 'Clear All?', variant: 'warning' });
    if (!ok) return;
    try {
      await notificationAPI.deleteAll();
      setNotifications([]);
      setUnreadCount(0);
      toast.success('All cleared');
    } catch { toast.error('Failed'); }
  };

  const sendAnnouncement = async () => {
    if (!announceForm.title.trim() || !announceForm.message.trim()) return toast.error('Title and message required');
    setSending(true);
    try {
      const r = await api.post('/notifications/announce', announceForm);
      toast.success(r.data.message || 'Announcement sent!');
      setAnnounceModal(false); setAnnounceForm({ title:'', message:'', targetRole:'all' });
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    setSending(false);
  };

  const timeAgo = (date) => {
    const s = Math.floor((Date.now() - new Date(date)) / 1000);
    if (s < 60)   return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s/60)}m ago`;
    if (s < 86400)return `${Math.floor(s/3600)}h ago`;
    return `${Math.floor(s/86400)}d ago`;
  };

  return (
    <div className="hr-page">
      {announceModal && (
        <div style={{ position:'fixed', inset:0, background:'#00000060', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div style={{ background:'#fff', borderRadius:20, padding:32, width:'100%', maxWidth:460 }}>
            <h3 style={{ margin:'0 0 20px', fontSize:18, fontWeight:800 }}>📢 Send Announcement</h3>
            <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:4 }}>Title *</label>
            <input style={{ width:'100%', padding:'10px 14px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:14, outline:'none', boxSizing:'border-box', marginBottom:12 }}
              value={announceForm.title} onChange={e=>setAnnounceForm({...announceForm,title:e.target.value})} placeholder="Announcement title"/>
            <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:4 }}>Message *</label>
            <textarea style={{ width:'100%', padding:'10px 14px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:14, outline:'none', boxSizing:'border-box', marginBottom:12, resize:'vertical', minHeight:100 }}
              value={announceForm.message} onChange={e=>setAnnounceForm({...announceForm,message:e.target.value})} placeholder="Write your announcement..."/>
            <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:4 }}>Send To</label>
            <select style={{ width:'100%', padding:'10px 14px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:14, outline:'none', boxSizing:'border-box', marginBottom:20 }}
              value={announceForm.targetRole} onChange={e=>setAnnounceForm({...announceForm,targetRole:e.target.value})}>
              <option value="all">Everyone</option>
              <option value="team">Team Members Only</option>
              <option value="manager">Managers Only</option>
              <option value="client">Clients Only</option>
            </select>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={()=>setAnnounceModal(false)} style={{ padding:'10px 20px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#f8fafc', fontWeight:600, cursor:'pointer' }}>Cancel</button>
              <button onClick={sendAnnouncement} disabled={sending} style={{ padding:'10px 20px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}><FiSend/>{sending?'Sending…':'Send Announcement'}</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1 style={{ margin: 0 }}>Notifications</h1>
          {isAdmin && <button onClick={()=>setAnnounceModal(true)} style={{ padding:'8px 16px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', fontWeight:600, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}><FiSend size={14}/> Announce</button>}
          {unreadCount > 0 && (
            <span style={{ background: '#f56565', color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: 13, fontWeight: 700 }}>
              {unreadCount} unread
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={load} title="Refresh"
            style={{ width: 36, height: 36, borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
            <FiRefreshCw size={15} />
          </button>
          {unreadCount > 0 && (
            <button onClick={handleMarkAllRead}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#22c55e' }}>
              <FiCheck size={14} /> Mark all read
            </button>
          )}
          {notifications.length > 0 && (
            <button onClick={handleDeleteAll}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1.5px solid #fee2e2', background: '#fff5f5', cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#ef4444' }}>
              <FiTrash2 size={14} /> Clear all
            </button>
          )}
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['all', 'unread'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '7px 20px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 600,
              background: filter === f ? 'var(--pm-gradient)' : '#f1f5f9',
              color: filter === f ? '#fff' : '#64748b',
              fontSize: 13, transition: 'all .15s',
            }}
          >
            {f === 'all' ? 'All' : `Unread (${unreadCount})`}
          </button>
        ))}
      </div>

      {/* Notification List */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#a0aec0' }}>Loading…</div>
        ) : notifications.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <FiBell size={48} style={{ color: '#cbd5e0', marginBottom: 12, display: 'block', margin: '0 auto 12px' }} />
            <p style={{ color: '#a0aec0', fontSize: 16 }}>
              {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
            </p>
          </div>
        ) : (
          notifications.map((n, idx) => {
            const meta = TYPE_META[n.type] || { icon: <FiBell />, color: '#a0aec0', label: n.type };
            return (
              <div
                key={n._id}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 20px',
                  borderBottom: idx < notifications.length - 1 ? '1px solid #f0f4f8' : 'none',
                  background: n.isRead ? 'transparent' : '#f7f8ff',
                  transition: 'background 0.2s',
                }}
              >
                {/* Icon */}
                <div style={{
                  width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', flexShrink: 0,
                  background: `${meta.color}18`, color: meta.color, fontSize: 17,
                }}>
                  {meta.icon}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: meta.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {meta.label}
                      </span>
                      <p style={{ margin: '2px 0 4px', fontWeight: n.isRead ? 400 : 600, color: '#2d3748', fontSize: 14 }}>
                        {n.title}
                      </p>
                      <p style={{ margin: 0, color: '#718096', fontSize: 13 }}>{n.message}</p>
                      {n.sender?.name && (
                        <p style={{ margin: '4px 0 0', color: '#a0aec0', fontSize: 12 }}>From: {n.sender.name}</p>
                      )}
                    </div>
                    <span style={{ color: '#a0aec0', fontSize: 12, flexShrink: 0 }}>{timeAgo(n.createdAt)}</span>
                  </div>

                  {/* Links & Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                    {n.link && (
                      <Link to={n.link} style={{ fontSize: 12, color: '#667eea', fontWeight: 600 }}>
                        View →
                      </Link>
                    )}
                    {!n.isRead && (
                      <button
                        onClick={() => handleMarkRead(n._id)}
                        style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', background: '#dcfce7', border: 'none', borderRadius: 6, padding: '3px 10px', cursor: 'pointer' }}
                      >
                        Mark read
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(n._id)}
                      style={{ fontSize: 11, fontWeight: 700, color: '#ef4444', background: '#fee2e2', border: 'none', borderRadius: 6, padding: '3px 10px', cursor: 'pointer' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Unread dot */}
                {!n.isRead && (
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#667eea', flexShrink: 0, marginTop: 6 }} />
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Notifications;
